import WorkflowSchema from '../models/workflow-schema.js';

export interface WorkflowCategory {
  name: string;
  schemas: WorkflowSchema[];
}

// The workflow schema data has no category/tag field, so categories are derived from keywords in
// the workflow name instead. Order matters - more specific keywords are checked before the broad
// "Company" catch-all so e.g. "select-company-to-transfer-node-cash" lands in Node Cash, not Company.
const CATEGORY_KEYWORDS: { name: string; match: RegExp }[] = [
  { name: 'Node Cash', match: /node-cash/ },
  { name: 'Public Ledger', match: /public-ledger/ },
  { name: 'Subscription', match: /subscription/ },
  { name: 'Onboarding', match: /onboard/ },
  { name: 'Company', match: /company|staff|reporting-to/ },
  { name: 'Payment', match: /payment/ },
];

const OTHER_CATEGORY = 'Other';

export function categorizeWorkflow(schema: WorkflowSchema): string {
  return CATEGORY_KEYWORDS.find(({ match }) => match.test(schema.name))?.name ?? OTHER_CATEGORY;
}

// Distinct tile background colour per category, in place of icons (several existing icon assets
// hardcode absolute fill colours or are embedded raster images, so they rendered invisible or
// wrong against a themed tile background - a flat colour sidesteps that entirely).
// Every fill keeps 3:1 against the white menu; green, amber and grey-mid don't, and red means destructive.
const CATEGORY_COLORS: { [category: string]: string } = {
  Company: 'var(--ns-theme-purple)',
  Subscription: 'color-mix(in srgb, var(--ns-theme-purple) 70%, var(--ns-theme-white))',
  'Public Ledger': 'var(--ns-theme-teal)',
  'Node Cash': 'color-mix(in srgb, var(--ns-theme-teal) 60%, var(--ns-theme-white))',
  Onboarding: 'color-mix(in srgb, var(--ns-theme-purple), var(--ns-theme-teal))',
  Payment: 'var(--ns-theme-type)',
  [OTHER_CATEGORY]: 'var(--ns-theme-grey-dark)',
};

export function categoryColor(category: string): string {
  return CATEGORY_COLORS[category] ?? CATEGORY_COLORS[OTHER_CATEGORY];
}

// Groups schemas by category, largest category first (ties broken alphabetically) so the most
// useful categories surface at the top of the grid.
export function categorizeWorkflows(schemas: WorkflowSchema[]): WorkflowCategory[] {
  const byCategory = new Map<string, WorkflowSchema[]>();

  schemas.forEach((schema) => {
    const category = categorizeWorkflow(schema);
    const existing = byCategory.get(category);
    if (existing) existing.push(schema);
    else byCategory.set(category, [schema]);
  });

  return Array.from(byCategory.entries())
    .map(([name, categorySchemas]) => ({ name, schemas: categorySchemas }))
    .sort((a, b) => b.schemas.length - a.schemas.length || a.name.localeCompare(b.name));
}
